const numberFormatter = new Intl.NumberFormat('en-US');

const dateTimeFormatOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
};

export const formatLocalDateTime = (date: Date): string =>
  date.toLocaleString(undefined, dateTimeFormatOptions);

export const formatNumber = (value: number): string => numberFormatter.format(value);

export const formatTimestamp = (epochSeconds: number | null | undefined): string => {
  if (!epochSeconds) {
    return 'Unknown';
  }

  const date = new Date(epochSeconds * 1000);
  if (!Number.isFinite(date.getTime())) {
    return 'Unknown';
  }

  return formatLocalDateTime(date);
};

export const toPercent = (value: number | null | undefined): number => {
  const clamped = Math.max(0, Math.min(1, value ?? 0));
  return Math.round(clamped * 100);
};

export type DatePrecision = 'day' | 'month' | 'year';

export interface DocumentDate {
  // Absent (with noDate=true) for the explicit "no date" sentinel: a document
  // the model processed but found no usable date on. Otherwise the date string
  // as written on the document.
  raw?: string | null;
  normalized?: string;
  year?: number;
  // month/day are null (not just absent) when explicitly unset by a reduced-
  // precision edit, matching the backend wire format.
  month?: number | null;
  day?: number | null;
  // Explicit precision: "day" | "month" | "year". The backend always sends it
  // (derived from the components); effectiveDatePrecision re-derives as a fallback
  // so display never depends on it being present.
  precision?: DatePrecision | null;
  // True when the document was processed but no date was found.
  noDate?: boolean;
  // Human-confirmation provenance (parallel to DocumentTaxonomy).
  confirmed?: boolean;
  confirmedBy?: string | null;
  confirmedAt?: string | null;
}

/** The date's effective precision. Prefers the explicit `precision` field; else
 * derives from which components are present (day > month > year), mirroring the
 * backend. Returns null for the no-date sentinel / empty date. */
export const effectiveDatePrecision = (date?: DocumentDate | null): DatePrecision | null => {
  if (!date || date.noDate) return null;
  if (date.precision) return date.precision;
  if (typeof date.day === 'number') return 'day';
  if (typeof date.month === 'number') return 'month';
  if (typeof date.year === 'number') return 'year';
  if (date.normalized) return 'day';
  return null;
};

export const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export const formatDocumentDate = (date: DocumentDate): string => {
  // Explicit "no date" — the model looked and found none.
  if (date.noDate) {
    return 'No date';
  }

  const precision = effectiveDatePrecision(date);

  // Render at the stored precision — never pad to a fabricated day/month.
  if (precision === 'year' && typeof date.year === 'number') {
    return String(date.year);  // "2024"
  }
  if (precision === 'month' && typeof date.year === 'number' && typeof date.month === 'number') {
    const m = MONTH_NAMES[date.month - 1] ?? '';
    return m ? `${m} ${date.year}` : String(date.year);  // "Mar 2024"
  }

  if (date.normalized) {
    // Full date: "Jan 15, 2024". Parse the Y-M-D components and render in UTC so
    // a local timezone behind UTC doesn't shift "2024-01-15" back to the 14th.
    const [y, m, d] = date.normalized.split('-').map(Number);
    if (y && m && d) {
      return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
      });
    }
  }
  if (typeof date.year === 'number') {
    // Day precision claimed but no normalized string — fall back to year.
    return String(date.year);
  }

  // Fallback to raw (may be empty if a malformed date object slipped through).
  return date.raw ?? 'No date';
};
